import {
  Add,
  Category,
  Inventory,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Box,
  Container,
  IconButton,
  InputAdornment,
  Tab,
  TablePagination,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  CategoryPricingModal,
  CategoryPricingTable,
  PricingStatisticsCards,
  ProductPricingModal,
  ProductPricingTable,
} from "../../components/backend/pricing";
import {
  useCategoryPricing,
  usePricingMutations,
  usePricingStatistics,
  useSpecificProductPricing,
} from "../../hooks/usePricing";

import AdminPage from "./AdminPage";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`pricing-tabpanel-${index}`}
      aria-labelledby={`pricing-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const PricingPage: React.FC = () => {
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(0);
  const [modalState, setModalState] = useState<{
    open: boolean;
    mode: "create" | "edit";
    type: "category" | "product";
    data?: any;
  }>({
    open: false,
    mode: "create",
    type: "category",
  });

  // Pagination state
  const [categoryPage, setCategoryPage] = useState(1);
  const [categoryPageSize, setCategoryPageSize] = useState(20);
  const [categorySearch, setCategorySearch] = useState("");

  const [productPage, setProductPage] = useState(1);
  const [productPageSize, setProductPageSize] = useState(20);
  const [productSearch, setProductSearch] = useState("");

  // React Query hooks with pagination
  const { data: categoryData, isLoading: categoryLoading } = useCategoryPricing(
    categoryPage,
    categoryPageSize,
    categorySearch
  );
  const { data: productData, isLoading: productLoading } =
    useSpecificProductPricing(productPage, productPageSize, productSearch);
  const { data: statisticsData, isLoading: statisticsLoading } =
    usePricingStatistics();
  const { deleteCategoryPricing, deleteProductPricing } = usePricingMutations();

  // Extract data - handle both paginated and non-paginated responses
  const categories = categoryData?.data?.items || categoryData?.data || [];
  const categoryPagination = categoryData?.data?.items
    ? {
        totalCount: categoryData.data.totalCount,
        totalPages: categoryData.data.totalPages,
        currentPage: categoryData.data.currentPage,
        hasNextPage: categoryData.data.hasNextPage,
        hasPreviousPage: categoryData.data.hasPreviousPage,
      }
    : null;

  const products = productData?.data?.items || productData?.data || [];
  const productPagination = productData?.data?.items
    ? {
        totalCount: productData.data.totalCount,
        totalPages: productData.data.totalPages,
        currentPage: productData.data.currentPage,
        hasNextPage: productData.data.hasNextPage,
        hasPreviousPage: productData.data.hasPreviousPage,
      }
    : null;
  const statistics = statisticsData?.data || null;

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const openModal = (
    mode: "create" | "edit",
    type: "category" | "product",
    data?: any
  ) => {
    setModalState({
      open: true,
      mode,
      type,
      data,
    });
  };

  const closeModal = () => {
    setModalState({
      open: false,
      mode: "create",
      type: "category",
    });
  };

  const handleSuccess = () => {
    closeModal();
  };

  const handleDeleteCategory = async (id: string) => {
    if (
      window.confirm(
        t("admin.pricing.confirmDeleteCategory")
      )
    ) {
      try {
        await deleteCategoryPricing.mutateAsync(id);
      } catch (error) {
        console.error("Error deleting category pricing:", error);
      }
    }
  };

  const handleDeleteProduct = async (id: string) => {
    if (
      window.confirm(
        t("admin.pricing.confirmDeleteProduct")
      )
    ) {
      try {
        await deleteProductPricing.mutateAsync(id);
      } catch (error) {
        console.error("Error deleting product pricing:", error);
      }
    }
  };

  return (
    <AdminPage>
      <Container maxWidth="xl">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {t("admin.pricing.title")}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t("admin.pricing.subtitle")}
          </Typography>
        </Box>

        {/* Statistics Cards */}
        <PricingStatisticsCards
          statistics={statistics}
          loading={statisticsLoading}
        />

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mt: 4 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="pricing tabs"
          >
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Inventory />
                  {t("admin.pricing.productPricing")}
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Category />
                  {t("admin.pricing.categoryPricing")}
                </Box>
              }
            />
          </Tabs>
        </Box>

        {/* Tab 0 – Product Pricing */}
        <TabPanel value={tabValue} index={0}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              gap: 2,
            }}
          >
            <Typography variant="h6">{t("admin.pricing.productSpecificRules")}</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <TextField
                size="small"
                placeholder={t("admin.pricing.searchProducts")}
                value={productSearch}
                onChange={(e) => {
                  setProductSearch(e.target.value);
                  setProductPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 250 }}
              />
              <Tooltip title={t("admin.pricing.addProductPricing")}>
                <IconButton
                  color="primary"
                  onClick={() => openModal("create", "product")}
                  sx={{
                    bgcolor: "primary.main",
                    color: "white",
                    "&:hover": { bgcolor: "primary.dark" },
                  }}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <ProductPricingTable
            products={products}
            loading={productLoading}
            onEdit={(product) => openModal("edit", "product", product)}
            onDelete={handleDeleteProduct}
          />
          {productPagination && (
            <TablePagination
              component="div"
              count={productPagination.totalCount}
              page={productPage - 1}
              onPageChange={(_, newPage) => setProductPage(newPage + 1)}
              rowsPerPage={productPageSize}
              onRowsPerPageChange={(e) => {
                setProductPageSize(parseInt(e.target.value, 10));
                setProductPage(1);
              }}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          )}
        </TabPanel>

        {/* Tab 1 – Category Pricing */}
        <TabPanel value={tabValue} index={1}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              gap: 2,
            }}
          >
            <Typography variant="h6">{t("admin.pricing.categoryRules")}</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <TextField
                size="small"
                placeholder={t("admin.pricing.searchCategories")}
                value={categorySearch}
                onChange={(e) => {
                  setCategorySearch(e.target.value);
                  setCategoryPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 250 }}
              />
              <Tooltip title={t("admin.pricing.addCategoryPricing")}>
                <IconButton
                  color="primary"
                  onClick={() => openModal("create", "category")}
                  sx={{
                    bgcolor: "primary.main",
                    color: "white",
                    "&:hover": { bgcolor: "primary.dark" },
                  }}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <CategoryPricingTable
            categories={categories}
            loading={categoryLoading}
            onEdit={(category) => openModal("edit", "category", category)}
            onDelete={handleDeleteCategory}
          />
          {categoryPagination && (
            <TablePagination
              component="div"
              count={categoryPagination.totalCount}
              page={categoryPage - 1}
              onPageChange={(_, newPage) => setCategoryPage(newPage + 1)}
              rowsPerPage={categoryPageSize}
              onRowsPerPageChange={(e) => {
                setCategoryPageSize(parseInt(e.target.value, 10));
                setCategoryPage(1);
              }}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          )}
        </TabPanel>

        {/* Modals */}
        <CategoryPricingModal
          open={modalState.open && modalState.type === "category"}
          mode={modalState.mode}
          category={modalState.mode === "edit" ? modalState.data : undefined}
          onClose={closeModal}
          onSuccess={handleSuccess}
        />

        <ProductPricingModal
          open={modalState.open && modalState.type === "product"}
          mode={modalState.mode}
          product={modalState.mode === "edit" ? modalState.data : undefined}
          onClose={closeModal}
          onSuccess={handleSuccess}
        />


      </Container>
    </AdminPage>
  );
};

export default PricingPage;
